/**
 * Social API
 * - 팔로우/언팔로우, 팔로워·팔로잉 목록, 개인화 피드, 컬렉션 관련 API 호출
 */
import apiClient from './client';

// ──────────────────────────────────────────────
// 공통 타입
// ──────────────────────────────────────────────

/** 커서 기반 페이지네이션 응답 */
export interface CursorPage<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
}

/** 팔로우 관계에 포함되는 사용자 요약 */
export interface UserSummary {
  _id: string;
  name: string;
  profileImage?: string;
}

/** 피드 아이템 (공개 앨범 요약) */
export interface FeedItem {
  _id: string;
  title: string;
  coverUrl?: string;
  owner: UserSummary;
  likesCount: number;
  createdAt: string;
}

/** 컬렉션 카드 미리보기용 앨범 메타 (BE에서 앞 4개 lookup 결과) */
export interface CollectionPreview {
  _id: string;
  title?: string;
  artist?: string;
  coverImageUrl?: string;
  printUnits?: Record<string, unknown>;
}

/** 컬렉션 */
export interface Collection {
  _id: string;
  name: string;
  description?: string;
  owner: string | { _id: string; name: string; profileImageUrl?: string };
  albums: string[];
  isPublic: boolean;
  coverUrl?: string;
  createdAt: string;
  updatedAt: string;
  albumCount?: number;
  /** 다른 사용자가 이 컬렉션을 가져가기(fork)할 수 있는지 여부. */
  allowFork?: boolean;
  /** 이 컬렉션이 fork된 원본 컬렉션 ID. */
  forkedFrom?: string;
  /** 카드 cover 그리드용 — 컬렉션 안 앨범 앞 4개 (BE aggregate 결과). */
  previews?: CollectionPreview[];
  /** 이 컬렉션의 총 좋아요 수 (getCollectionDetail 응답에 포함). */
  likeCount?: number;
  /** 현재 로그인 사용자가 이 컬렉션을 좋아요했는지 여부 (비로그인 시 false). */
  liked?: boolean;
}

/** 페이지네이션 응답 */
export interface PageResponse<T> {
  data: T[];
  total: number;
  page: number;
  pages: number;
}

/**
 * 컬렉션 상세 응답 — 앨범 항목.
 * - status: 'active' | 'hidden_by_owner' | 'deleted'
 *   - active: 정상 접근 가능
 *   - hidden_by_owner: 원작자가 비공개로 전환 (스냅샷만 표시)
 *   - deleted: 원본이 삭제됨 (스냅샷만 표시)
 * - hidden/deleted 항목은 title/artist/coverImageUrl/originalOwnerName 등 스냅샷 필드만 신뢰 가능
 */
export interface CollectionAlbumItem {
  _id: string;
  title: string;
  artist: string;
  coverImageUrl?: string;
  printUnits?: Record<string, unknown>;
  likeCount?: number;
  createdAt: string;
  /** 앨범 라이프사이클 상태 — BE 신규 필드. 미정의면 active 취급. */
  status?: 'active' | 'hidden_by_owner' | 'deleted';
  /** 스냅샷 보존된 원작자 표시 이름 (deleted/hidden 상태일 때 원작자 표기용). */
  originalOwnerName?: string;
  /** 컬렉션에 추가된 시각 (스냅샷). */
  addedAt?: string;
  /** 원작자가 이 앨범의 복제를 허용하는지 여부. */
  allowDuplicate?: boolean;
}

/** 앨범이 populate된 컬렉션 상세 */
export interface CollectionWithAlbums extends Collection {
  albums: CollectionAlbumItem[];
  owner?: { _id: string; name: string; profileImageUrl?: string };
}

/** 컬렉션 생성/수정 요청 바디 */
export interface CollectionPayload {
  name: string;
  description?: string;
  isPublic?: boolean;
  coverUrl?: string;
  allowFork?: boolean;
}

// ──────────────────────────────────────────────
// 팔로우
// ──────────────────────────────────────────────

/** 특정 사용자의 팔로워/팔로잉 수 + 현재 사용자의 팔로우 여부 */
export interface UserSocialStats {
  followerCount: number;
  followingCount: number;
  isFollowing: boolean;
}

export const getUserSocialStats = async (userId: string): Promise<UserSocialStats> => {
  const res = await apiClient.get<UserSocialStats>(`/social/users/${userId}/stats`);
  return res.data;
};

/** 사용자 팔로우 (204 No Content) */
export const followUser = async (userId: string): Promise<void> => {
  await apiClient.post(`/social/follow/${userId}`);
};

/** 사용자 언팔로우 (204 No Content) */
export const unfollowUser = async (userId: string): Promise<void> => {
  await apiClient.delete(`/social/follow/${userId}`);
};

/** 내 팔로워 목록 (커서 페이지네이션) */
export const getFollowers = async (
  cursor?: string,
  limit = 20,
): Promise<CursorPage<UserSummary>> => {
  const res = await apiClient.get<CursorPage<UserSummary>>('/social/followers', {
    params: { cursor, limit },
  });
  return res.data;
};

/** 내 팔로잉 목록 (커서 페이지네이션) */
export const getFollowing = async (
  cursor?: string,
  limit = 20,
): Promise<CursorPage<UserSummary>> => {
  const res = await apiClient.get<CursorPage<UserSummary>>('/social/following', {
    params: { cursor, limit },
  });
  return res.data;
};

// ──────────────────────────────────────────────
// 피드
// ──────────────────────────────────────────────

/** 개인화 피드 조회 (팔로잉 사용자의 최신 공개 앨범) */
export const getFeed = async (
  cursor?: string,
  limit = 20,
): Promise<CursorPage<FeedItem>> => {
  const res = await apiClient.get('/social/feed', {
    params: { cursor, limit },
  });
  const d = res.data;
  return {
    items: d.items ?? d.data ?? [],
    nextCursor: d.nextCursor,
    hasMore: d.hasMore ?? false,
  };
};

// ──────────────────────────────────────────────
// 컬렉션
// ──────────────────────────────────────────────

/** 컬렉션 생성 */
export const createCollection = async (payload: CollectionPayload): Promise<Collection> => {
  const res = await apiClient.post<Collection>('/social/collections', payload);
  return res.data;
};

/** 내 컬렉션 목록 조회 */
export const getMyCollections = async (): Promise<Collection[]> => {
  const res = await apiClient.get<Collection[]>('/social/collections');
  return res.data;
};

/** 컬렉션 수정 */
export const updateCollection = async (
  id: string,
  payload: CollectionPayload,
): Promise<Collection> => {
  const res = await apiClient.put<Collection>(`/social/collections/${id}`, payload);
  return res.data;
};

/** 컬렉션에 앨범 추가 */
export const addAlbumToCollection = async (
  collectionId: string,
  albumId: string,
): Promise<Collection> => {
  const res = await apiClient.post<Collection>(
    `/social/collections/${collectionId}/albums/${albumId}`,
  );
  return res.data;
};

/** 컬렉션에서 앨범 제거 */
export const removeAlbumFromCollection = async (
  collectionId: string,
  albumId: string,
): Promise<Collection> => {
  const res = await apiClient.delete<Collection>(
    `/social/collections/${collectionId}/albums/${albumId}`,
  );
  return res.data;
};

/**
 * 다중 앨범을 단일 컬렉션에 일괄 추가.
 * - 백엔드는 added/failed 분리 응답: 권한·중복 등 사유로 거부된 항목은 failed에 들어감.
 * - 단일 추가는 기존 addAlbumToCollection 사용 권장.
 */
export interface BulkAddAlbumsResult {
  added: string[];
  failed: { albumId: string; reason: string }[];
}

export const bulkAddAlbumsToCollection = async (
  collectionId: string,
  albumIds: string[],
): Promise<BulkAddAlbumsResult> => {
  const res = await apiClient.post<BulkAddAlbumsResult>(
    `/social/collections/${collectionId}/albums/bulk`,
    { albumIds },
  );
  return res.data;
};

/**
 * 다중 앨범을 단일 컬렉션에서 일괄 제거.
 * - 존재하지 않는 albumId는 무시되며 에러 발생하지 않음.
 * - 반환: { removed: string[]; failed: { albumId: string; reason: string }[] }
 */
export interface BulkRemoveAlbumsResult {
  removed: string[];
  failed: { albumId: string; reason: string }[];
}

export const bulkRemoveAlbumsFromCollection = async (
  collectionId: string,
  albumIds: string[],
): Promise<BulkRemoveAlbumsResult> => {
  const res = await apiClient.delete<BulkRemoveAlbumsResult>(
    `/social/collections/${collectionId}/albums/bulk`,
    { data: { albumIds } },
  );
  return res.data;
};

/**
 * 특정 앨범이 어떤 컬렉션에 속해 있는지 조회 (membership).
 * - Save Sheet 진입 시 "이미 추가된 컬렉션"을 표시하기 위함.
 */
export interface CollectionsMembershipResponse {
  collectionIds: string[];
}

export const getCollectionsMembership = async (
  albumId: string,
): Promise<CollectionsMembershipResponse> => {
  const res = await apiClient.get<CollectionsMembershipResponse>(
    '/social/collections/membership',
    { params: { albumId } },
  );
  return res.data;
};

/** 컬렉션 삭제 */
export const deleteCollection = async (collectionId: string): Promise<void> => {
  await apiClient.delete(`/social/collections/${collectionId}`);
};

/** 컬렉션 상세 조회 (앨범 populate) */
export const getCollectionDetail = async (collectionId: string): Promise<CollectionWithAlbums> => {
  const res = await apiClient.get<CollectionWithAlbums>(`/social/collections/${collectionId}`);
  return res.data;
};

/** 컬렉션 가져오기(fork) 응답 */
export interface ForkCollectionResult {
  collectionId: string;
  forkedAlbumIds: string[];
  skippedCount: number;
}

/** 컬렉션 가져오기 — 내 컬렉션으로 복제 */
export const forkCollection = async (collectionId: string): Promise<ForkCollectionResult> => {
  const res = await apiClient.post<ForkCollectionResult>(`/social/collections/${collectionId}/fork`);
  return res.data;
};

/** 공개 컬렉션 목록 조회 */
export const getPublicCollections = async (params?: {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
}): Promise<PageResponse<Collection>> => {
  const res = await apiClient.get<PageResponse<Collection>>('/social/collections/public', { params });
  return res.data;
};

// ──────────────────────────────────────────────
// 컬렉션 좋아요
// ──────────────────────────────────────────────

/** 컬렉션 좋아요 추가 */
export const likeCollection = async (
  collectionId: string,
): Promise<{ likeCount: number; liked: true }> => {
  const res = await apiClient.post<{ likeCount: number; liked: true }>(
    `/social/collections/${collectionId}/like`,
  );
  return res.data;
};

/** 컬렉션 좋아요 취소 */
export const unlikeCollection = async (
  collectionId: string,
): Promise<{ likeCount: number; liked: false }> => {
  const res = await apiClient.delete<{ likeCount: number; liked: false }>(
    `/social/collections/${collectionId}/like`,
  );
  return res.data;
};
